(() => {
  if (customElements.get('co-nissan-sd-notice')) return;
  class NissanSDNotice extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const listen = (target, type, handler, options = {}) => target?.addEventListener(type, handler, { ...options, signal: this.controller.signal });
      try { this.config = JSON.parse(this.querySelector('[data-sd-config]').textContent); } catch (_) { return; }
      this.form = this.closest('form');
      this.trigger = this.querySelector('[data-sd-trigger]');
      this.panel = this.querySelector('[data-sd-popover]');
      this.closeButton = this.querySelector('[data-sd-close]');
      this.image = this.querySelector('[data-sd-image]');
      if (!this.form || !this.trigger || !this.panel || !this.image) return;
      this.hoverMedia = window.matchMedia('(hover: hover) and (pointer: fine)');
      this.opened = false;
      this.pinned = false;
      listen(this.trigger, 'pointerenter', e => {
        clearTimeout(this.leaveTimer);
        if (this.hoverMedia.matches && e.pointerType !== 'touch') this.open(false);
      });
      listen(this.trigger, 'pointerleave', () => this.scheduleClose());
      listen(this.panel, 'pointerenter', () => clearTimeout(this.leaveTimer));
      listen(this.panel, 'pointerleave', () => this.scheduleClose());
      listen(this.trigger, 'focus', () => {
        if (!this.suppressFocus && this.trigger.matches(':focus-visible')) this.open(false);
      });
      const focusOut = () => queueMicrotask(() => {
        if (document.activeElement !== this.trigger && !this.panel.contains(document.activeElement)) this.close(false);
      });
      listen(this.trigger, 'focusout', focusOut);
      listen(this.panel, 'focusout', focusOut);
      listen(this.trigger, 'click', e => {
        e.preventDefault();
        if (this.opened && this.pinned) this.close(true);
        else this.open(true);
      });
      listen(this.closeButton, 'click', e => { e.preventDefault(); this.close(true); });
      listen(this.panel, 'toggle', e => {
        if (e.newState === 'closed') this.setClosed();
      });
      listen(document, 'pointerdown', e => {
        if (this.opened && !this.panel.contains(e.target) && !this.trigger.contains(e.target)) this.close(false);
      });
      listen(document, 'keydown', e => {
        if (e.key === 'Escape' && this.opened) {
          e.preventDefault(); e.stopPropagation(); this.close(true);
        }
      });
      const onVariant = e => {
        const variant = e.detail?.variant || e.detail;
        if (variant?.id) this.refresh(variant.id);
      };
      listen(document, 'variant:change', onVariant);
      listen(document, 'product:variant-change', onVariant);
      listen(document, 'change', e => {
        if (e.target?.form === this.form || this.form.contains(e.target)) queueMicrotask(() => this.refresh());
      });
      // Kalles may set its hidden select after a swatch click without a native change event.
      listen(this.form, 'click', e => {
        if (e.target?.closest?.('[data-swatch-item]')) setTimeout(() => this.refresh(), 0);
      });
      const fromURL = () => this.refresh(new URL(location.href).searchParams.get('variant'));
      listen(window, 'popstate', fromURL);
      listen(window, 'co:urlchange', fromURL);
      listen(window, 'resize', () => this.opened && this.position(), { passive: true });
      listen(window, 'scroll', () => this.opened && this.position(), { passive: true });
      listen(window.visualViewport, 'resize', () => this.opened && this.position(), { passive: true });
      this.refresh();
    }
    disconnectedCallback() {
      clearTimeout(this.leaveTimer);
      this.controller?.abort();
      this.controller = null;
    }
    refresh(id) {
      const selected = id || this.form.querySelector('select[name="id"], input[name="id"]')?.value || this.dataset.variant;
      const group = this.config.variants[String(selected || '')];
      const asset = group && this.config.images[group];
      if (this.variant !== String(selected)) this.close(false);
      this.variant = String(selected);
      this.hidden = !asset;
      if (!asset) return;
      const x = Number(asset.x), y = Number(asset.y);
      this.ready = !!asset.src && !asset.src.includes('__') && Number.isFinite(x) && Number.isFinite(y);
      this.trigger.disabled = !this.ready;
      if (!this.ready) return;
      this.image.src = asset.src;
      this.image.alt = asset.alt;
      this.image.width = Number(asset.width) || 1200;
      this.image.height = Number(asset.height) || 800;
      const targetX = Math.max(2, Math.min(98, x));
      const targetY = Math.max(2, Math.min(98, y));
      const startX = targetX > 50 ? Math.max(12, targetX - 27) : Math.min(88, targetX + 27);
      const startY = Math.min(88, targetY + 25);
      const line = `M ${startX} ${startY} L ${targetX} ${targetY}`;
      this.querySelector('[data-sd-arrow]').setAttribute('d', line);
      this.querySelector('[data-sd-arrow-border]').setAttribute('d', line);
    }
    open(pin) {
      if (this.hidden || !this.ready) return;
      clearTimeout(this.leaveTimer);
      this.pinned = this.pinned || pin;
      if (!this.opened) {
        this.panel.hidden = false;
        this.opened = true;
        this.trigger.setAttribute('aria-expanded', 'true');
        if (typeof this.panel.showPopover === 'function') {
          try { this.panel.showPopover({ source: this.trigger }); }
          catch (_) { this.panel.removeAttribute('popover'); }
        }
      }
      this.position();
      if (pin && !this.hoverMedia.matches) this.closeButton.focus({ preventScroll: true });
    }
    setClosed() {
      this.opened = false;
      this.pinned = false;
      this.panel.hidden = true;
      this.trigger.setAttribute('aria-expanded', 'false');
    }
    close(restoreFocus) {
      clearTimeout(this.leaveTimer);
      if (!this.panel) return;
      if (this.opened && typeof this.panel.hidePopover === 'function' && this.panel.matches(':popover-open')) this.panel.hidePopover();
      this.setClosed();
      if (restoreFocus && !this.hidden) {
        this.suppressFocus = true;
        this.trigger.focus({ preventScroll: true });
        this.suppressFocus = false;
      }
    }
    scheduleClose() {
      clearTimeout(this.leaveTimer);
      this.leaveTimer = setTimeout(() => {
        if (!this.pinned && document.activeElement !== this.trigger && !this.panel.contains(document.activeElement)) this.close(false);
      }, 200);
    }
    position() {
      const viewport = window.visualViewport;
      const width = viewport?.width || window.innerWidth;
      const height = viewport?.height || window.innerHeight;
      const offsetX = viewport?.offsetLeft || 0, offsetY = viewport?.offsetTop || 0;
      const anchor = this.trigger.getBoundingClientRect();
      const box = this.panel.getBoundingClientRect();
      const left = Math.max(offsetX + 16, Math.min(anchor.left, offsetX + width - box.width - 16));
      const below = anchor.bottom + 10;
      const proposedTop = below + box.height <= offsetY + height - 16 ? below : anchor.top - box.height - 10;
      const top = Math.max(offsetY + 16, Math.min(proposedTop, offsetY + height - box.height - 16));
      this.panel.style.left = `${left}px`;
      this.panel.style.top = `${top}px`;
    }
  }
  customElements.define('co-nissan-sd-notice', NissanSDNotice);
})();
